<template>
  <div>
    <VenueNearbyStaticMap
      v-if="showStatic"
      :venue="venue"
      :venues="venues"
      :destination="destination"
      class="cursor-pointer"
      @click="staticMapClick"
    />
    <div v-else>
      <div
        class="z-30 bg-white"
        :class="{
          relative: !showFull,
          'pb-56%': !showFull,
          'mb-3': !showFull,
          fixed: showFull,
          'inset-0': showFull,
        }"
        :style="{ bottom: `${mapBottom}px` }"
      >
        <div v-if="!showFull && ['sm', 'md'].includes($screen.name)" class="absolute inset-0 z-10" @click="showFull = true" />
        <a
          v-if="showFull"
          href="#"
          class="absolute right-4 top-3 z-20 flex size-8 rounded bg-white text-center text-white"
          @click.prevent="showFull = false"
        >
          <svg
            class="m-auto block"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M8 9.46788L1.46794 16L0 14.532L6.53206 7.99992L0.000156405 1.46796L1.4681 0L8 6.53196L14.5319 0L15.9998 1.46796L9.46794 7.99992L16 14.532L14.5321 16L8 9.46788Z" fill="#333333" />
          </svg>
        </a>
        <div class="absolute inset-0">
          <div ref="map" class="size-full" />
        </div>
      </div>

      <div class="fixed inset-0 top-auto z-10 bg-white lg:hidden" :style="{ height: `${mapBottom}px` }" :class="{ hidden: !showFull }">
        <VenueMobile v-if="clickedVenue" :venue="clickedVenue" show-score @close="clickedVenue = null" />
      </div>
    </div>
  </div>
</template>

<script>
import merge from 'lodash/merge'
import debounce from 'lodash/debounce'
import { removeClass, toggleClass } from '~/utils/class'

import VenueMobile from '~/components/venues/VenueMobile'
import VenueNearbyStaticMap from '~/components/venue/VenueNearbyStaticMap'

let mapboxgl

const defaultMarkerActive = import('assets/images/markers/marker.svg')
const defaultMarker = import('assets/images/markers/marker-fade.svg')

const markersActive = [
  import('assets/images/markers/marker-hotel.svg'),
  import('assets/images/markers/marker-restaurant.svg'),
  import('assets/images/markers/marker-attraction.svg'),
]

const markers = [
  import('assets/images/markers/marker-hotel-fade.svg'),
  import('assets/images/markers/marker-restaurant-fade.svg'),
  import('assets/images/markers/marker-attraction-fade.svg'),
]

export default {
  components: { VenueNearbyStaticMap, VenueMobile },

  props: {
    destination: { type: Object, required: true },
    venues: { type: Array, required: true },
    venue: { type: Object, required: true },
  },

  data: () => ({
    showStatic: true,
    showFull: false,
    mapStyle: 'mapbox://styles/tripexpertweb/ckcdpjifn06ru1impw321xsqi',
    map: null,
    markers: [],
    preventShowSearchArea: false,
    mapBottom: 0,
    clickedVenue: null,
  }),

  computed: {
    allVenues () {
      const venue = merge({}, this.venue, { mainVenue: true })
      const venues = this.venues.slice()
      venues.push(venue)
      return venues
    },

    bounds () {
      return [
        [
          Math.min(...this.allVenues.map(v => v.longitude)),
          Math.min(...this.allVenues.map(v => v.latitude)),
        ],
        [
          Math.max(...this.allVenues.map(v => v.longitude)),
          Math.max(...this.allVenues.map(v => v.latitude)),
        ],
      ]
    },
  },

  watch: {
    showStatic (val) {
      if (! val) {
        this.$nextTick(() => this.initMap())
      }
    },

    showFull (val) {
      setTimeout(() => this.map.resize(), 50)
      if (! val) {
        this.clickedVenue = false
      }
    },

    clickedVenue (val) {
      if (! ['sm', 'md'].includes(this.$screen.name)) { return }
      if (val) {
        this.mapBottom = this.$screen.name === 'sm' ? 300 : 215
      } else {
        this.mapBottom = 0
        this.toggleMarkerActive() // Deactivate active marker
      }
      this.resizeMap()
    },
  },

  methods: {
    async initMap () {
      mapboxgl = (await import('mapbox-gl')).default
      mapboxgl.accessToken = process.env.MAPBOX_API_TOKEN
      this.map = new mapboxgl.Map({
        container: this.$refs.map,
        style: 'mapbox://styles/tripexpertweb/ckcdpjifn06ru1impw321xsqi',
        center: [this.destination.longitude, this.destination.latitude],
        zoom: 11.15,
      })
      this.map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-left')
      this.addMarkers()
      this.fitBounds()
    },

    addMarkers () {
      if (! process.client) { return }
      if (this.markers.length) {
        this.markers.forEach(marker => marker.remove())
        this.markers = []
      }

      this.allVenues.forEach((venue, index) => {
        // create DOM element for the ma rker
        const el = document.createElement('div')
        el.innerHTML = `<div class="marker-inner">
          <img class="img-active" src="${venue.mainVenue ? defaultMarkerActive : markersActive[venue.venue_type_id - 1]}" alt="">
          <img class="img-inactive" src="${venue.mainVenue ? defaultMarker : markers[venue.venue_type_id - 1]}" alt="">
        </div>`
        el.id = `marker-${venue.id}`
        el.className = 'marker'
        el.addEventListener('click', () => this.toggleMarkerActive(el))

        const marker = new mapboxgl.Marker(el) // new mapboxgl.Marker(el, {offset:[-25, -25]})
          .setLngLat([venue.longitude, venue.latitude])
          .setPopup(new mapboxgl.Popup({ offset: 60, closeButton: false, closeOnMove: true, anchor: 'bottom' })
            .setHTML(`
              <div class="flex items-center">
                <div>
                  <h3 class="font-whitney-condensed"><a href="/${venue.path}">${venue.name}</a></h3>
                </div>
                <a href="/${venue.path}" class="chevron-right">
                  <svg width="10" height="16" viewBox="0 0 10 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M1.29936 16L-5.6797e-08 14.7006L6.70063 8L-6.42585e-07 1.29937L1.29936 -5.6797e-08L9.29936 8L1.29936 16Z" fill="white"/>
                  </svg>
                </a>
              </div>
           `))
          .addTo(this.map)

        marker.getElement().addEventListener('click', () => {
          this.markerClick(index)
        })

        this.markers.push(marker)
      })
    },

    toggleMarkerActive (el) {
      this.markers.forEach((m) => {
        removeClass(m.getElement(), 'active')
      })
      if (el) { toggleClass(el, 'active') }
    },

    fitBounds () {
      this.map.fitBounds(this.bounds, { padding: 30 })
    },

    markerClick (index) {
      this.clickedVenue = this.venues[index]
    },

    resizeMap: debounce(function () {
      this.map.resize()
    }, 10),

    staticMapClick () {
      this.showStatic = false
      if (['sm', 'md'].includes(this.$screen.name)) { this.showFullMap = true }
    },
  },
}
</script>

<style lang="scss" scoped>
  :deep() {
    .mapboxgl-popup {
      @media (max-width: 911px) { display: none; }
    }

    .mapboxgl-popup-tip { display: none; }

    .mapboxgl-popup-content {
      @apply p-4 bg-slate text-white rounded-lg;
      h3 {
        @apply text-lg uppercase font-medium;
        letter-spacing: 0.1em;
      }
      p {
        @apply text-sm;
      }
      .chevron-right {
        @apply block pl-6 pr-1;
      }
    }

    .marker {
      @apply w-12 h-12 font-medium text-body text-center cursor-pointer;
      font-size: 22px; margin-top: -24px; line-height: 30px;
      .img-active { display: none; }

      &.active {
        @apply z-10;
        .img-active { display: block; }
        .img-inactive { display: none; }
      }
    }
    .marker-inner {
      position: relative; width: 100%; height: 100%;
      img { max-height: 100%; margin: auto; }
    }
    .maker-score {
      @apply absolute inset-0 pt-1 leading-normal;
    }

    .mapboxgl-ctrl-top-left .mapboxgl-ctrl {
      @apply mt-3;
    }
  }
</style>
